/* Progressive enhancement for the cart shipping-row country selector: hides
   the native select and opens a searchable popup instead. The native select
   stays in the DOM as the no-JS fallback and as the form field that submits. */

if (!customElements.get('country-search')) {
  customElements.define(
    'country-search',
    class CountrySearch extends HTMLElement {
      connectedCallback() {
        if (this.dataset.enhanced) return;
        this.form = this.querySelector('form');
        this.select = this.querySelector('select[name="country_code"]');
        this.panel = this.querySelector('[data-country-search-panel]');
        this.input = this.querySelector('.country-search__input');
        this.list = this.querySelector('.country-search__list');
        this.empty = this.querySelector('.country-search__empty');
        if (!this.form || !this.select || !this.panel) return;
        this.dataset.enhanced = 'true';
        this.classList.add('country-search--enhanced');

        const code = this.querySelector('.cart-shipping__country-code');
        this.trigger = document.createElement('button');
        this.trigger.type = 'button';
        this.trigger.className = 'country-search__trigger';
        this.trigger.setAttribute('aria-haspopup', 'listbox');
        this.trigger.setAttribute('aria-expanded', 'false');
        if (this.dataset.triggerLabel) {
          this.trigger.setAttribute('aria-label', this.dataset.triggerLabel);
        }
        code.parentNode.insertBefore(this.trigger, code);
        this.trigger.appendChild(code);

        this.onDocumentPointerDown = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        this.trigger.addEventListener('click', () => {
          this.panel.hidden ? this.open() : this.close();
        });
        this.trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown' && this.panel.hidden) {
            event.preventDefault();
            this.open();
          }
        });
        this.input.addEventListener('input', () => this.filter(this.input.value));
        this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      }

      open() {
        if (!this.options) this.buildList();
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.input.value = '';
        this.filter('');
        this.input.focus({ preventScroll: true });
        document.addEventListener('pointerdown', this.onDocumentPointerDown);
      }

      close(refocus) {
        if (this.panel.hidden) return;
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        if (refocus) this.trigger.focus();
      }

      buildList() {
        const aliases = {
          GB: 'uk england britain great britain',
          US: 'usa america united states',
          AE: 'uae emirates dubai',
          TR: 'turkey',
          NL: 'holland',
          CZ: 'czech republic',
          MO: 'macau',
          KR: 'korea',
          CH: 'swiss',
        };
        const fragment = document.createDocumentFragment();
        let index = 0;
        const addOption = (option) => {
          const item = document.createElement('li');
          item.id = `${this.select.id}-opt-${index++}`;
          item.setAttribute('role', 'option');
          item.dataset.value = option.value;
          item.dataset.search = `${option.value} ${option.textContent} ${aliases[option.value] || ''}`.toLowerCase();
          item.textContent = option.textContent.trim();
          item.setAttribute('aria-selected', option.value === this.select.value ? 'true' : 'false');
          item.addEventListener('click', () => this.pick(option.value));
          fragment.appendChild(item);
        };
        Array.from(this.select.children).forEach((child) => {
          if (child.tagName === 'OPTGROUP') {
            const header = document.createElement('li');
            header.className = 'country-search__group';
            header.setAttribute('role', 'presentation');
            header.textContent = child.label;
            fragment.appendChild(header);
            Array.from(child.children).forEach(addOption);
          } else if (child.tagName === 'OPTION') {
            addOption(child);
          }
        });
        this.list.appendChild(fragment);
        this.options = Array.from(this.list.querySelectorAll('[role="option"]'));
      }

      filter(query) {
        const q = query.trim().toLowerCase();
        let visible = 0;
        this.options.forEach((item) => {
          const show = !q || item.dataset.search.includes(q);
          item.hidden = !show;
          if (show) visible++;
        });
        this.list.querySelectorAll('.country-search__group').forEach((header) => {
          let el = header.nextElementSibling;
          let hasVisible = false;
          while (el && !el.classList.contains('country-search__group')) {
            if (!el.hidden) {
              hasVisible = true;
              break;
            }
            el = el.nextElementSibling;
          }
          header.hidden = !hasVisible;
        });
        this.empty.hidden = visible > 0;
        const first = q
          ? this.visibleOptions()[0]
          : this.options.find((item) => item.getAttribute('aria-selected') === 'true');
        this.setActive(first || null);
      }

      visibleOptions() {
        return this.options.filter((item) => !item.hidden);
      }

      setActive(item) {
        if (this.active) this.active.classList.remove('is-active');
        this.active = item;
        if (item) {
          item.classList.add('is-active');
          this.input.setAttribute('aria-activedescendant', item.id);
          item.scrollIntoView({ block: 'nearest' });
        } else {
          this.input.removeAttribute('aria-activedescendant');
        }
      }

      onInputKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
          return;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          if (this.active && !this.active.hidden) this.pick(this.active.dataset.value);
          return;
        }
        if (event.key === 'Tab') {
          this.close();
          return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const visible = this.visibleOptions();
          if (!visible.length) return;
          const current = visible.indexOf(this.active);
          const next =
            event.key === 'ArrowDown'
              ? visible[Math.min(current + 1, visible.length - 1)]
              : visible[Math.max(current - 1, 0)];
          this.setActive(next);
        }
      }

      pick(value) {
        if (value === this.select.value) {
          this.close(true);
          return;
        }
        this.select.value = value;
        this.form.submit();
      }
    }
  );
}
